import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import Helper from "util/Helper";
import AuthenticationService from "../../../../../service/auth/AuthenticationService";
import { MODES } from "../../navigo-module/helper/appointmentConstants";

// document-service folder types — its `type` field doubles as the folder id.
export const DOCUMENT_FOLDER_MEDICAL_RECORDS = 8; // "Medical records"
export const DOCUMENT_FOLDER_OTHER_MEDIA = 6; // "Other media"

export class CartDashboardUtils {
  constructor() {
    this.apiService = new APIService();
    this.helper = new Helper();
    this.authenticationService = new AuthenticationService();
  }

  fetchRefundsByFilter = (body, onResponse, onError) => {
    let url = APIConfig.refund.refundsByFilter;
    return this.apiService.post(url, body, onResponse, onError);
  };

  /**
   * Fetch carts by filter
   */
  fetchCartsByFilter = (body, onResponse, onError) => {
    let url = APIConfig.cartDashboard.fetchCartsByFilter;
    return this.apiService.post(url, body, onResponse, onError);
  };

  /**
   * Fetch the service-type → modes mapping used to populate the "Requested
   * service mode" filter on the cart verification dashboard. Mirrors the
   * Navigo dashboard's mode filter (same endpoint, same flatten-to-unique
   * usage downstream).
   */
  getModes = (onResponse, onError) => {
    let url = APIConfig.myPlan.navigo.getModes;
    return this.apiService.get(url, onResponse, onError);
  };

  /**
   * Fetch service providers by filter (used to resolve provider name -> id
   * for the cart dashboard "Service provider" filter). Returns { providers: [...] }.
   */
  fetchServiceProvidersByFilter = (body, onResponse, onError) => {
    let url =
      APIConfig.myPlan.serviceProviderDashboard.fetchServiceProvidersByFilter;
    return this.apiService.post(url, body, onResponse, onError);
  };

  /**
   * Fetch the accountCode for a plan via the plans/filters API.
   * The real cart-by-id API does not return accountCode, so it is derived
   * from the cart's planId here. Returns res.plans[0].accountCode (or null),
   * plus the plan record itself as a second argument: this lookup returns the
   * plan whatever its status, so it is the one place that can tell an expired
   * plan apart from a missing one.
   */
  fetchAccountCodeByPlanId = (planId, onResponse, onError) => {
    let url = APIConfig.myPlan.assignedPlans.getAssignedPlansByFilter;
    let body = { pageNum: 1, pageSize: 1, planIds: [planId] };
    return this.apiService.post(
      url,
      body,
      (res) => {
        const plan = res?.plans?.[0] ?? null;
        onResponse(plan?.accountCode ?? null, plan);
      },
      onError,
    );
  };

  /**
   * Fetch the plan's wallet rules via the plans/filters API. The cart's planSelected
   * (PlanAppResponse from getPlansForPid) does NOT carry walletDetails, so both fields are
   * derived from the planId here.
   *
   * onResponse receives `{ isWalletIncluded, allowedFamilyRelations }`:
   *  - `isWalletIncluded` — true only when the plan explicitly has a wallet.
   *  - `allowedFamilyRelations` — the relation codes the wallet may be spent on. Relation 0
   *    (self) is never listed; a family member outside this list cannot use the wallet, which
   *    is what drives the "wallet unavailable for this member" notice. Always an array, so
   *    callers can `.includes()` without guarding.
   */
  fetchPlanWalletRulesByPlanId = (planId, onResponse, onError) => {
    let url = APIConfig.myPlan.assignedPlans.getAssignedPlansByFilter;
    let body = { pageNum: 1, pageSize: 1, planIds: [planId] };
    return this.apiService.post(
      url,
      body,
      (res) => {
        const walletDetails = res?.plans?.[0]?.walletDetails;
        onResponse({
          isWalletIncluded: walletDetails?.isWalletIncluded === true,
          allowedFamilyRelations: Array.isArray(
            walletDetails?.allowedFamilyRelations,
          )
            ? walletDetails.allowedFamilyRelations
            : [],
        });
      },
      onError,
    );
  };

  /**
   * Fetch cart by ID.
   *
   * Options:
   *  - `syncFromHead: true` — backend first overwrites the patient_carts
   *    user-input fields with the head cart_payment_request snapshot before
   *    returning. Used by VERIFY open so each verify session starts from the
   *    app user's original submission rather than whatever the reviewer left
   *    behind on a prior visit.
   *  - `includeEvents: true` — backend returns the same flat cart doc with the
   *    cart's hydrated, append-only `eventHistory` log (payment / refund events,
   *    newest-first) merged in as an extra field. Used by the cart verification
   *    screen to populate the Payment details table. Without the flag the same
   *    flat cart doc is returned with no `eventHistory`.
   *  - `includePrices: true` — backend returns the cart's frozen per-service price
   *    break-up as `serviceBreakup: [{serviceId, serviceName, price}]`, read from the
   *    head cart_payment_request's servicePrices map. Only a booked cart has one; a
   *    DRAFT simply comes back with no `serviceBreakup`. See `applyServiceBreakup`.
   *
   * The dashboard's mode-determination read passes none of them.
   */
  fetchCartById = (
    cartId,
    onResponse,
    onError,
    { syncFromHead = false, includeEvents = false, includePrices = false } = {},
  ) => {
    let url = APIConfig.cartDashboard.fetchCartById + "/" + cartId;
    const params = [];
    if (syncFromHead) params.push("syncFromHead=true");
    if (includeEvents) params.push("includeEvents=true");
    if (includePrices) params.push("includePrices=true");
    if (params.length > 0) {
      url += "?" + params.join("&");
    }
    return this.apiService.get(
      url,
      (res) => {
        onResponse(res);
      },
      onError,
    );
  };

  /**
   * Upsert the patient's DRAFT cart (CREATE / EDIT-DRAFT). PUT
   * /api/v1/cart/patient/{patientId}. The backend creates a DRAFT cart if none
   * exists, else updates it (partial — only provided fields). serviceIds are
   * replaced wholesale. Mirrors the app's modifyActiveCart: every call carries
   * patientId; `body` supplies groupServiceId (the selected parent service)
   * plus whatever else is being changed (planId, serviceIds, bookedFor,
   * addressId, serviceProviderId, slot fields). undefined keys are dropped by
   * JSON.stringify, so unset fields are not sent.
   */
  modifyActiveCart = (patientId, body, onResponse, onError) => {
    let url = APIConfig.cartDashboard.modifyActiveCart + "/" + patientId;
    const payload = {
      ...body,
      patientId: patientId,
    };
    return this.apiService.put(url, payload, onResponse, onError);
  };

  /**
   * Fetch the patient's active DRAFT cart by patientId
   * (GET /api/v1/cart/patient/{patientId}).
   *
   * Used by the "Add New" flow: when the operator picks a Patient ID, this
   * returns the patient's existing DRAFT (the same single DRAFT shared with the
   * app) so the User-details form can auto-populate instead of starting empty.
   * No planId/groupServiceId query params are sent, so the DRAFT is returned
   * regardless of its (plan, groupService) context. Response is the
   * { cart, serviceIdDetailsMapping } shape; cart.id === 0 / absent means the
   * patient has no DRAFT (start a fresh cart).
   */
  fetchActiveCartByPatientId = (patientId, onResponse, onError) => {
    let url = APIConfig.cartDashboard.modifyActiveCart + "/" + patientId;
    return this.apiService.get(url, onResponse, onError);
  };

  /**
   * Update a cart by cartId as an admin/ops user (VERIFY mode add/remove on a
   * PENDING_REVIEW / ON_HOLD cart, which the patient PUT cannot touch).
   * PUT /api/v1/cart/admin/{cartId}. Only the provided fields are updated;
   * serviceIds are replaced wholesale.
   */
  updateCartByAdmin = (cartId, body, onResponse, onError) => {
    let url = APIConfig.cartDashboard.updateCartByAdmin + "/" + cartId;
    return this.apiService.put(url, body, onResponse, onError);
  };

  /**
   * Preview the payment delta for a cart under review (VERIFY). POST
   * /api/v1/cart/admin/{cartId}/payment-sublimit?wallet=true|false. Returns the
   * paid vs target composition, signed delta, and refund / newPayment plans so
   * the bridge user can see what approving will collect or refund.
   */
  getCartPaymentSublimit = (cartId, useWallet, onResponse, onError) => {
    let url =
      APIConfig.cartDashboard.cartPaymentSublimit +
      "/" +
      cartId +
      "/payment-sublimit?wallet=" +
      (useWallet ? "true" : "false");
    return this.apiService.post(url, {}, onResponse, onError);
  };

  /**
   * Approve a cart under review (VERIFY). POST
   * /api/v1/cart/admin/{cartId}/approve. Body carries expectedHeadRevision
   * (optimistic lock, from the sublimit preview's headRevision) and a
   * paymentLinkRequest { paymentDetails (the preview sublimit verbatim),
   * paymentLink (notify channels), useWallet }. useWallet is the source of
   * truth the backend uses to pick the wallet-vs-razorpay split. Fires refunds,
   * wallet debit and/or a Razorpay top-up link, then creates the appointment.
   */
  approveCart = (cartId, body, onResponse, onError) => {
    let url = APIConfig.cartDashboard.approveCart + "/" + cartId + "/approve";
    return this.apiService.post(url, body, onResponse, onError);
  };

  /**
   * Fetch the list of options for the "Mark on hold" dropdown
   * (GET /api/v1/cart/admin/hold-reasons). Returns only active entries by
   * default; pass includeInactive=true to also get retired ones.
   * Response item shape (extra fields ignored by the dropdown):
   *   { id: number, label: string, isActive: boolean,
   *     requiresDescription: boolean, createdAt, updatedAt, createdBy, updatedBy }
   */
  fetchHoldReasons = (includeInactive, onResponse, onError) => {
    let url = APIConfig.cartDashboard.holdReasons;
    if (includeInactive) {
      url += "?includeInactive=true";
    }
    return this.apiService.get(url, onResponse, onError);
  };

  /**
   * Mark a PENDING_REVIEW cart as ON_HOLD (POST /admin/{cartId}/hold).
   * Body: { reason: number, description?: string } where `reason` is the
   * HoldReason id from fetchHoldReasons; `description` is mandatory when the
   * chosen entry has requiresDescription. 409 means the cart is no longer in
   * PENDING_REVIEW (caller should refresh).
   */
  holdCart = (cartId, body, onResponse, onError) => {
    let url = APIConfig.cartDashboard.holdCart + "/" + cartId + "/hold";
    return this.apiService.post(url, body, onResponse, onError);
  };

  /**
   * Resume an ON_HOLD cart back to PENDING_REVIEW (POST /admin/{cartId}/resume).
   * No body. 409 means the cart is no longer ON_HOLD.
   */
  resumeCart = (cartId, onResponse, onError) => {
    let url = APIConfig.cartDashboard.resumeCart + "/" + cartId + "/resume";
    return this.apiService.post(url, {}, onResponse, onError);
  };

  /**
   * Fetch the reject-reason options for the "Reject order" dropdown
   * (GET /api/v1/cart/admin/reject-reasons). Backed by the reject_reasons
   * collection, so ops can add/retire entries without a deploy. Returns only
   * active entries by default; pass includeInactive=true to also get retired
   * ones — required when resolving an id on an already-rejected cart, since
   * the entry it points at may since have been retired.
   * Response item shape (extra fields ignored by the dropdown):
   *   { id: number, label: string, isActive: boolean,
   *     requiresDescription: boolean, createdAt, updatedAt, createdBy, updatedBy }
   */
  fetchRejectReasons = (includeInactive, onResponse, onError) => {
    let url = APIConfig.cartDashboard.rejectReasons;
    if (includeInactive) {
      url += "?includeInactive=true";
    }
    return this.apiService.get(url, onResponse, onError);
  };

  /**
   * Reject a cart (POST /admin/{cartId}/reject). Body:
   *   { expectedHeadRevision: number, reason: number, description?: string }
   * `reason` is the RejectReason id from fetchRejectReasons; `description` is
   * the free text, mandatory when the chosen entry has requiresDescription.
   * 409 returns the current head — the caller should retry with
   * `expectedHeadRevision` set to the head's revision (or 0 if the response is
   * `{"headRevision": 0}`).
   */
  rejectCart = (cartId, body, onResponse, onError) => {
    let url = APIConfig.cartDashboard.rejectCart + "/" + cartId + "/reject";
    return this.apiService.post(url, body, onResponse, onError);
  };

  /**
   * Maps a file extension (no leading dot) to the document-service `format` int.
   * document-service branches on this: only format 4 gets a generated PDF thumbnail,
   * and formats 1-3 are returned in a folder's `images` list while 4 goes to `pdfs` —
   * so a PDF sent as format 1 comes back to the patient app as a broken image.
   */
  getDocumentFormat = (extension) => {
    switch (extension) {
      case "jpg":
      case "jpeg":
        return 1;
      case "png":
        return 2;
      case "gif":
        return 3;
      case "pdf":
        return 4;
      default:
        return 0;
    }
  };

  /**
   * Upload document
   * POST /v1/documents
   * Returns full document object with id, url, etc.
   *
   * `patientId` is who the row is FILED UNDER — document-service's read path is
   * patient-scoped, so this decides who can ever see it. `folderType` is its folder
   * (document-service's `type` doubles as the folder id): 8 "Medical records" for the
   * booking's own patient, 6 "Other media" for the account holder's second copy of a
   * family member's file. Both match what the patient app uploads.
   */
  uploadDocument = (
    file,
    patientId,
    folderType = DOCUMENT_FOLDER_MEDICAL_RECORDS,
    onResponse,
    onError,
  ) => {
    let url = APIConfig.documentApis.uploadDoc;
    let formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", file.name);
    formData.append(
      "document",
      JSON.stringify({
        patientId: patientId,
        title: file.name,
        description: "",
        type: folderType,
        // Lowercased because file pickers hand back names like "report.PDF".
        format: this.getDocumentFormat(
          file.name.split(".").pop().toLowerCase(),
        ),
        status: 4,
      }),
    );
    formData.append("patientId", patientId);
    return this.apiService.postWithFormData(url, formData, onResponse, onError);
  };

  /**
   * Search tests
   */
  searchTests = (query, searchParams, onResponse, onError) => {
    let url = APIConfig.cartDashboard.searchTests;
    const body = {
      pageNum: 1,
      pageSize: 20,
      planId: searchParams.planId,
      groupServiceId: searchParams.groupServiceId,
      patientId: searchParams.patientId,
      includingText: query,
    };
    return this.apiService.post(
      url,
      body,
      (res) => {
        const services = res?.services || [];
        onResponse(this._mapChildServices(services));
      },
      onError,
    );
  };

  /**
   * Fetch the full list of available tests/scans for the plan+group (empty
   * includingText). Used to prepopulate the cart search dropdown so the
   * reviewer can click to add without having to guess a search term — the same
   * default list the patient app shows on the "Prescribed diagnostics" screen.
   *
   * This list is also the corpus the dropdown fuzzy-searches locally (see
   * DocCartView), so it has to be complete: the API pages at 20 by default, and
   * a truncated first page would silently hide matches. Walk the pages until
   * totalCount is covered.
   */
  fetchAvailableTests = (searchParams, onResponse, onError) => {
    let url = APIConfig.cartDashboard.searchTests;
    const pageSize = 100;
    const maxPages = 20; // backstop; 2000 child services is far beyond any group
    const collected = [];

    const fetchPage = (pageNum) => {
      const body = {
        pageNum,
        pageSize,
        planId: searchParams.planId,
        groupServiceId: searchParams.groupServiceId,
        patientId: searchParams.patientId,
        includingText: "",
      };
      return this.apiService.post(
        url,
        body,
        (res) => {
          const services = res?.services || [];
          collected.push(...services);
          const totalCount = res?.totalCount ?? collected.length;
          const hasMore =
            services.length > 0 &&
            collected.length < totalCount &&
            pageNum < maxPages;
          if (hasMore) {
            fetchPage(pageNum + 1);
            return;
          }
          onResponse(this._mapChildServices(collected));
        },
        onError,
      );
    };

    return fetchPage(1);
  };

  /**
   * Fetch child services (tests) by their IDs.
   * Uses the same searchTests endpoint with serviceIds filter instead of text search.
   */
  fetchChildServicesByIds = (serviceIds, searchParams, onResponse, onError) => {
    let url = APIConfig.cartDashboard.searchTests;
    const body = {
      pageNum: 1,
      pageSize: 100,
      planId: searchParams.planId,
      groupServiceId: searchParams.groupServiceId,
      patientId: searchParams.patientId,
      serviceIds: serviceIds,
    };
    return this.apiService.post(
      url,
      body,
      (res) => {
        const services = res?.services || [];
        onResponse(this._mapChildServices(services));
      },
      onError,
    );
  };

  /**
   * Split a booked cart's frozen `serviceBreakup` into the parts the cart panel renders.
   *
   * The break-up prices every service the cart was booked at — including ones that are not cart
   * items: the home-sample collection charge is priced as its own row but never appears in
   * cart.serviceIds. So matched rows stamp `price` onto the test they belong to, and unmatched rows
   * are returned separately to be listed under the tests. Keeping both means the rows still add up
   * to `total`, which is the amount the patient actually paid.
   *
   * Returns the items untouched (and no rows/total) when the cart has no break-up — i.e. anything
   * not yet booked, which keeps its live-coverage pricing.
   */
  applyServiceBreakup = (items, serviceBreakup) => {
    const breakup = Array.isArray(serviceBreakup) ? serviceBreakup : [];
    if (breakup.length === 0) {
      return { items, extraRows: [], total: null };
    }

    const priceByServiceId = new Map(
      breakup.map((row) => [row.serviceId, row.price]),
    );
    const pricedItems = (items || []).map((item) => ({
      ...item,
      price: priceByServiceId.has(item.id)
        ? priceByServiceId.get(item.id)
        : null,
    }));

    const itemIds = new Set((items || []).map((item) => item.id));
    const extraRows = breakup.filter((row) => !itemIds.has(row.serviceId));
    const total = breakup.reduce((sum, row) => sum + (row.price || 0), 0);

    return { items: pricedItems, extraRows, total };
  };

  /**
   * Maps raw ChildService[] from API to the shape used by the UI
   */
  _mapChildServices = (services) => {
    return services.map((s) => {
      const tags = s.serviceTags || [];
      return {
        id: s.id,
        name: s.name,
        groupServiceId: s.groupServiceId,
        serviceName: s.serviceName,
        serviceTypeId: s.serviceTypeId,
        serviceSubtypeId: s.serviceSubtypeId,
        serviceTypeName: s.serviceTypeName,
        description: s.description || "",
        descriptionLong: s.descriptionLong || "",
        subTestCount: s.includedTestList ? s.includedTestList.length : 0,
        // Resolve each test id (in includedTestList order) to its rich details
        // from includedTestListMap so the details dialog can show the test
        // name / code instead of the raw id. Falls back to the id when a map
        // entry is missing.
        includes: (s.includedTestList || []).map((testId) => {
          const t = (s.includedTestListMap || {})[testId];
          return t
            ? {
                id: t.id ?? testId,
                name: t.name ?? testId,
                zylaSubTestCode: t.zylaSubTestCode ?? "",
                loincCode: t.loincCode ?? "",
              }
            : { id: testId, name: testId, zylaSubTestCode: "", loincCode: "" };
        }),
        includedTestListMap: s.includedTestListMap || {},
        tags,
        // serviceTags carries "Fasting required" / "Fasting not required".
        fastingRequired: tags.some(
          (t) => String(t).toLowerCase() === "fasting required",
        ),
        isEligibleForWallet: s.isEligibleForWallet === true,
      };
    });
  };

  /**
   * Fetch providers for the given cart items.
   */
  fetchProviders = (body, onResponse, onError) => {
    let url = APIConfig.cartDashboard.fetchProviders;
    const payload = {
      patientId: body.patientId,
      planId: body.planId,
      groupServiceId: body.groupServiceId,
      serviceIds: body.serviceIds,
      providerStatus: true,
      serviceProviderMappingStatus: true,
      shareProviderAvailability: true,
    };
    // addressId lets the backend compute each provider's distance from the
    // patient's booking address (distanceInKm in the response).
    if (body.addressId != null) {
      payload.addressId = body.addressId;
    }
    // Who the booking is FOR. services-service swaps patientId for bookedFor when it is
    // non-zero, and everything patient-scoped in the response is then judged against that
    // profile: `serviceModeDetails[].isNotEligible` (age/gender rules) and
    // `walletEligibleTotalPrice`. Omitting it does not error — it silently judges the
    // ACCOUNT HOLDER, so a family member's cart gets someone else's eligibility verdict.
    // 0 is the cart's "self" sentinel and means the account holder anyway, so it is dropped
    // here exactly as the backend would ignore it.
    if (body.bookedFor) {
      payload.bookedFor = body.bookedFor;
    }
    if (body.geolocationFilter) {
      payload.geolocationFilter = body.geolocationFilter;
    }
    return this.apiService.post(
      url,
      payload,
      (res) => {
        // The backend omits providerCoverageWithMode (or sends it empty) when no
        // provider can serve this cart at this address — a 200 with nothing bookable.
        // Route it to onError so callers surface it instead of confirming the cart
        // with an empty provider dropdown.
        const coverage = res?.providerCoverageWithMode;
        if (!Array.isArray(coverage) || coverage.length === 0) {
          onError({ message: "No provider found!" });
          return;
        }
        onResponse(this._mapProviderResponse(res));
      },
      onError,
    );
  };

  /**
   * Maps ChildServiceProviderResponse to the shape used by the UI.
   */
  _mapProviderResponse = (res) => {
    const providers = (res.providerCoverageWithMode || []).map((p) => {
      const addr = p.providerAddress || {};
      // Full centre address shown for centre-visit providers (matches the app):
      // housing, area, landmark, city, district, state, pincode.
      const addressParts = [
        addr.housing,
        addr.area,
        addr.landmark,
        addr.city,
        addr.district,
        addr.state,
        addr.pincode,
      ].filter(Boolean);

      const isHomeSample = p.assignedServiceMode === MODES.HOME_SAMPLE;

      return {
        id: p.providerId,
        name: p.providerName,
        address: addressParts.join(", "),
        providerAddress: addr,
        supportedTests: p.providedServiceIds || [],
        missingTests: p.missingServiceIds || [],
        isHomeSample,
        assignedServiceMode: p.assignedServiceMode || "",
        coveragePercentage: p.coveragePercentage || 0,
        // Distance of the provider's centre from the patient's booking address
        // (km). Backend sends null when not applicable (e.g. home-sample).
        distanceInKm: p.distanceInKm ?? null,
        googleMapLink: addr.googleMapLink || "",
        totalPrice: p.totalPrice || 0,
        serviceModeDetails: p.serviceModeDetails || [],
        minBookingDays: p.minBookingDays || 0,
        maxAdvBookingDays: p.maxAdvBookingDays || 30,
        bannerText: isHomeSample
          ? "Lab tests and scans will be scheduled at your booking address"
          : "Lab tests and scans will be conducted at selected centre address",
      };
    });

    return {
      providers,
      serviceDetails: res.serviceDetails || {},
    };
  };

  /**
   * Fetch available slots by date range.
   */
  getSlots = (body, onResponse, onError) => {
    let url = APIConfig.cartDashboard.availableSlotsByRange;
    const payload = {
      serviceProviderID: body.serviceProviderID,
      planId: body.planId,
      patientId: body.patientId,
      bookedFor: body.bookedFor,
      serviceId: body.serviceId,
      childServiceIds: body.childServiceIds,
    };
    if (body.addressId) {
      payload.addressId = body.addressId;
    }
    if (body.geolocationFilter) {
      payload.geolocationFilter = body.geolocationFilter;
    }
    return this.apiService.post(
      url,
      payload,
      (res) => {
        onResponse(this._mapSlotsResponse(res));
      },
      onError,
    );
  };

  /**
   * Maps SlotDateRangeResponse to the shape used by SelectDateSlotSection.
   * availableCount is how many more bookings the slot can still take — it is
   * shown on the chip, NOT used to gate selection: any slot the API returns for
   * an available day is treated as bookable (same as the patient app).
   * `time` carries the backend's range label ("12:30 - 1:00 PM") and is left
   * empty on vendor paths (Apollo/Redcliffe/OneMG never run FilterBookedSlots,
   * which is what populates label). The section formats `value` itself in that
   * case, so both paths render in the same 12-hour style.
   * Input:  { response: [{ dayStr, dayNum, dayDate, isSlotAvailable, slots: [{key, value, label, availableCount}] }] }
   * Output: { dates: [{month, day, weekday, dayDate}], slots: { 0: [{time, value, key, availableCount}], ... } }
   */
  _mapSlotsResponse = (res) => {
    const dayEntries = res.response || [];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const dates = [];
    const slotsMap = {};

    dayEntries.forEach((entry, idx) => {
      // Parse month from dayDate (YYYY-MM-DD)
      const monthIndex = entry.dayDate
        ? parseInt(entry.dayDate.split("-")[1], 10) - 1
        : 0;
      dates.push({
        month: months[monthIndex] || "",
        day: entry.dayNum,
        weekday: entry.dayStr,
        dayDate: entry.dayDate,
      });

      if (entry.isSlotAvailable && entry.slots) {
        slotsMap[idx] = entry.slots.map((s) => ({
          time: s.label || "",
          value: s.value,
          key: s.key,
          availableCount: s.availableCount,
        }));
      } else {
        slotsMap[idx] = [];
      }
    });

    return { dates, slots: slotsMap };
  };
}

export default CartDashboardUtils;
